import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Shield, Star, MessageCircle, Users, Eye, Heart, TrendingUp, XCircle, Loader2, Calendar, Clock } from 'lucide-react';
import { premiumAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import BackButton from '../components/ui/BackButton';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PricingPage = () => {
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { isAuthenticated, loadUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadPricingSettings();
    if (isAuthenticated) {
      loadPremiumStatus();
    }
  }, [isAuthenticated]);

  const loadPricingSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/payment/public`);
      if (response.ok) {
        const data = await response.json();
        setPricingSettings(data);
      }
    } catch (error) {
      console.error('Failed to load pricing settings:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const loadPremiumStatus = async () => {
    try {
      const data = await premiumAPI.getStatus();
      setPremiumStatus(data);
    } catch (error) {
      console.error('Failed to load premium status:', error);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await premiumAPI.cancelSubscription();
      
      toast({
        title: "Subscription Cancelled",
        description: "Your premium subscription has been cancelled successfully.",
      });
      
      await loadUser();
      await loadPremiumStatus();
      
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  // Format price from settings
  const formatPrice = () => {
    if (!pricingSettings) return { symbol: 'CA$', amount: '9.99', full: 'CA$9.99' };
    
    const { price_amount, price_currency } = pricingSettings;
    const currencySymbols = {
      'CAD': 'CA$',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'AUD': 'A$',
      'PKR': 'Rs '
    };
    const symbol = currencySymbols[price_currency] || price_currency + ' ';
    const amount = price_amount?.toFixed(2) || '9.99';
    
    return {
      symbol,
      amount,
      whole: Math.floor(price_amount || 9.99),
      decimal: ((price_amount || 9.99) % 1).toFixed(2).substring(2),
      full: `${symbol}${amount}`
    };
  };

  const price = formatPrice();
  const planName = pricingSettings?.plan_name || 'Premium';
  const planDuration = pricingSettings?.plan_duration || '1 Year';
  const freeTrialEnabled = pricingSettings?.free_trial_enabled ?? true;
  const freeTrialDays = pricingSettings?.free_trial_days || 30;

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to subscribe to premium",
        variant: "destructive",
      });
      navigate('/login');
      return;
    }

    if (premiumStatus?.isPremium) {
      toast({
        title: "Already Premium!",
        description: "You already have an active premium subscription",
      });
      return;
    }

    // Navigate directly to payment page - it will fetch settings itself
    navigate('/payment');
  };

  const features = [
    { icon: Users, text: 'Unlimited profile views' },
    { icon: MessageCircle, text: 'Send unlimited messages' },
    { icon: TrendingUp, text: 'Advanced search filters' },
    { icon: Star, text: 'Verified premium badge' },
    { icon: Shield, text: 'Priority customer support' },
    { icon: Crown, text: 'Stand out from others — your profile appears more visible and attractive in searches and listings' }
  ];

  if (loadingPricing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="w-full px-2 py-6">
          <BackButton />
        </div>
      </div>

      <div className="w-full px-2 py-16">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl mb-6 shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4 tracking-tight">
            Upgrade to {planName}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed px-4">
            Get more freedom to connect, message, and find your ideal match faster — with verified profiles and tools made for serious users.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="w-full px-2 mb-16">
          <div className="bg-white border-2 border-gray-200 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
            
            {/* PAID PREMIUM USER - Show active status, NO trial banner */}
            {premiumStatus?.isPremium && !premiumStatus?.isFreeTrial && (
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Crown className="w-5 h-5" />
                  <span className="font-bold text-lg">You have Premium Access</span>
                </div>
                <p className="text-sm opacity-90">
                  {premiumStatus.expiryDate 
                    ? `${planDuration === '1 Year' ? 'Yearly plan' : 'Monthly plan'} • Valid until ${new Date(premiumStatus.expiryDate).toLocaleDateString()}`
                    : `${planDuration === '1 Year' ? 'Yearly plan' : 'Monthly plan'} • Auto-renews on next cycle`
                  }
                </p>
              </div>
            )}

            {/* FREE TRIAL USER - Show countdown (only if daysRemaining exists) */}
            {premiumStatus?.isFreeTrial && premiumStatus?.daysRemaining !== null && premiumStatus?.daysRemaining !== undefined && (
              <div className={`px-6 py-4 text-center ${
                premiumStatus.daysRemaining <= 3 
                  ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
              }`}>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Crown className="w-5 h-5" />
                  <span className="font-bold text-lg">
                    {premiumStatus.daysRemaining <= 3 
                      ? `Only ${premiumStatus.daysRemaining} day${premiumStatus.daysRemaining !== 1 ? 's' : ''} left on your free trial!`
                      : `Your free trial ends in ${premiumStatus.daysRemaining} days`
                    }
                  </span>
                </div>
                <p className="text-sm opacity-90">
                  {premiumStatus.daysRemaining <= 3 
                    ? 'Upgrade now to keep your premium features'
                    : 'Upgrade anytime to continue after your trial'
                  }
                </p>
              </div>
            )}

            {/* TRIAL EXPIRED USER - Show upgrade message */}
            {premiumStatus?.freeTrialUsed && !premiumStatus?.isPremium && (
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="w-5 h-5" />
                  <span className="font-bold text-lg">Your free trial has ended</span>
                </div>
                <p className="text-sm opacity-90">Upgrade now to continue enjoying premium features</p>
              </div>
            )}

            {/* Card Header */}
            <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-8 text-center border-b border-amber-200">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4 shadow-md">
                <Crown className="w-4 h-4" />
                <span>{planName.toUpperCase()} MEMBERSHIP</span>
              </div>
              
              <div className="mb-2">
                <span className="text-6xl font-bold text-gray-900">{price.symbol}{price.whole}</span>
                <span className="text-3xl font-semibold text-gray-700">.{price.decimal}</span>
                <span className="text-xl text-gray-600 ml-2">/{planDuration.toLowerCase()}</span>
              </div>
              
              <p className="text-gray-600 font-medium">
                {planDuration === '1 Year' ? 'Billed annually' : `Billed ${planDuration.toLowerCase()}`} • Cancel anytime
              </p>
              
              {/* Show billing breakdown */}
              {planDuration === '1 Year' ? (
                <div className="mt-3 inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Check className="w-4 h-4" />
                  Just {price.symbol}{((pricingSettings?.price_amount || 9.99) / 12).toFixed(2)}/month — billed yearly
                </div>
              ) : (
                <div className="mt-3 inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                  <Check className="w-4 h-4" />
                  {price.full}/month — billed monthly
                </div>
              )}
            </div>

            {/* Card Body */}
            <div className="p-8 md:p-12">
              
              {/* Features List */}
              <div className="space-y-4 mb-6">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600 stroke-[3]" />
                    </div>
                    <div className="flex items-center gap-3 flex-1">
                      <feature.icon className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-900 font-medium text-lg">{feature.text}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Privacy Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-10">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Note:</strong> Some features like profile view tracking or contact access are restricted to protect privacy. Payments are non-refundable once billing starts.
                </p>
              </div>

              {/* Action Button - Simple, one action based on status */}
              {premiumStatus?.isPremium && !premiumStatus?.isFreeTrial ? (
                // Active paid subscriber - show cancel option
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={cancelling}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-5 px-8 rounded-2xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-lg flex items-center justify-center gap-2 touch-manipulation"
                  type="button"
                >
                  <XCircle className="w-5 h-5" />
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              ) : (
                // Not premium (trial user, expired trial, or new user) - show upgrade with GOLD button
                <button
                  onClick={handleSubscribe}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold py-5 px-8 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl text-lg flex items-center justify-center gap-2"
                >
                  <Crown className="w-5 h-5" />
                  Get {planName} - {price.full}/{planDuration}
                </button>
              )}

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-8 mt-8 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="font-medium">Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">No Hidden Fees</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="w-full px-2">
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Choose {planName}?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">5x Faster Matching</h3>
                <p className="text-gray-600">Find your perfect match significantly faster with premium features</p>
              </div>

              <div className="text-center">
                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Unlimited Contact</h3>
                <p className="text-gray-600">Connect with anyone without restrictions or limitations</p>
              </div>

              <div className="text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Stand Out</h3>
                <p className="text-gray-600">Premium badge makes your profile more attractive and trustworthy</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Cancel Subscription Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription?"
        message="Are you sure you want to cancel your premium subscription? You will lose access to all premium features immediately."
        confirmText="Yes, Cancel"
        cancelText="Keep Premium"
        variant="danger"
      />
    </div>
  );
};

export default PricingPage;
